// TooltipComparison.jsx

import React from "react";
import { Divider, Stack, Tooltip, Typography } from "@mui/material";

import SmallPercentage from "../SmallPercentage";

import { fns } from "../../helpers/common";
import {
    COMPARISON_CUSTOM,
    COMPARISON_FILTER,
    COMPARISON_PREVIOUS_MONTH,
    COMPARISON_PREVIOUS_WEEK,
    COMPARISON_PREVIOUS_YEAR,
    COMPARISON_SAME_TIME_LAST_YEAR,
    DATATYPE_RESDATE,
    DEFAULT_COMPARISON,
    DISPLAY_ELEMENT_HELPER,
} from "../../helpers/filters";
import Colours from "../../helpers/colours";
import {
    formatDate,
    subtractOneMonth,
    subtractOneWeek,
    subtractOneYear,
} from "../../helpers/dates";
import { useSelector } from "react-redux";

const TooltipComparison = ({
    metricName,
    comparisonType,
    comparisonCustom,
    calculateComparisonDate = true,
    fromDay,
    toDay,
    value,
    valueOld,
    comparisonMeta = null,
    dataType,
    children,
    setSelected = () => {},
    show = true,
    renderContent = null,
    extraContent = null,
    valueFormat = {},
}) => {
    const { id: hotelID } = useSelector((state) => state.hotelID);

    const isTargetComparison = comparisonMeta?.isTarget === true;
    let comparison_label = isTargetComparison
        ? "Target"
        : COMPARISON_FILTER[comparisonType ?? DEFAULT_COMPARISON].label;

    const setLabel = (date) => {
        if (isTargetComparison) {
            return formatDate(date);
        }

        if (calculateComparisonDate) {
            switch (comparisonType) {
                case COMPARISON_PREVIOUS_YEAR:
                    return subtractOneYear(date, true);
                case COMPARISON_SAME_TIME_LAST_YEAR:
                    return subtractOneYear(date, true);
                case COMPARISON_PREVIOUS_MONTH:
                    return subtractOneMonth(date);
                case COMPARISON_PREVIOUS_WEEK:
                    return subtractOneWeek(date);
                default:
                    return subtractOneYear(date, true);
            }
        }

        return formatDate(date);
    };

    const today = new Date();

    const shouldShowSameTimeLastYearCutoff =
        calculateComparisonDate &&
        !isTargetComparison &&
        comparisonType === COMPARISON_SAME_TIME_LAST_YEAR &&
        new Date(fromDay) <= today &&
        today < new Date(toDay);

    const comparisonPeriodToDay =
        shouldShowSameTimeLastYearCutoff && dataType === DATATYPE_RESDATE
            ? today
            : toDay;

    const reservationsUntilDay = shouldShowSameTimeLastYearCutoff
        ? today
        : null;

    // If tooltip is disabled, just render children
    if (!show) return children;

    const targetRequested =
        comparisonMeta?.requestedGranularityMode &&
        comparisonMeta.requestedGranularityMode !== "Off";

    const baseComparisonText = !isNaN(
        Number(comparisonMeta?.baseComparisonValue),
    )
        ? fns(hotelID, comparisonMeta.baseComparisonValue, valueFormat)
        : "No previous value";

    const targetExtra = comparisonMeta?.isTarget ? (
        <>
            <Divider sx={{ my: 1, borderColor: "rgba(0,0,0,0.08)" }} />
            <Stack spacing={0.5}>
                <Typography fontSize="0.95em" fontWeight={"bold"}>
                    Target details
                </Typography>

                <Typography fontSize="0.9em">
                    Applied: {comparisonMeta.appliedCombo || "-"}
                </Typography>

                <Stack spacing={0.25}>
                    <Typography fontSize="0.9em">Periods used:</Typography>

                    {(comparisonMeta.periodsUsed ?? []).length > 0 ? (
                        comparisonMeta.periodsUsed.map((period) => (
                            <Typography
                                key={`${period.validFrom}-${period.mode}-${period.percentageN}`}
                                fontSize="0.85em"
                                color="text.secondary"
                            >
                                {period.mode} · {period.validFrom} ·{" "}
                                {Number(period.percentageN ?? 0)}%
                            </Typography>
                        ))
                    ) : (
                        <Typography fontSize="0.85em" color="text.secondary">
                            No periods found
                        </Typography>
                    )}
                </Stack>

                <Typography fontSize="0.9em">
                    Without target: {baseComparisonText}
                </Typography>
            </Stack>
        </>
    ) : targetRequested && comparisonMeta?.reason ? (
        <>
            <Divider sx={{ my: 1, borderColor: "rgba(0,0,0,0.08)" }} />
            <Typography fontSize="0.85em" color="text.secondary">
                Target not applied: {comparisonMeta.reason}
            </Typography>
        </>
    ) : null;

    // Default content (kept identical to existing behavior)
    const defaultContent = (
        <Stack
            direction="column"
            justifyContent="center"
            alignItems="stretch"
            spacing={1}
            m={2}
            my={1}
        >
            <Typography fontSize="1.25em" fontWeight={"bold"}>
                {comparison_label}
            </Typography>

            <Typography fontSize="1em" fontWeight={"light"}>
                {comparisonType !== COMPARISON_CUSTOM
                    ? `${setLabel(fromDay)} - ${setLabel(comparisonPeriodToDay)}`
                    : `${formatDate(
                          new Date(comparisonCustom.fromDay ?? new Date()),
                      )} - ${formatDate(
                          new Date(comparisonCustom.toDay ?? new Date()),
                      )}`}

                {reservationsUntilDay && (
                    <>
                        <br />
                        <Typography
                            component="span"
                            fontSize="0.85em"
                            color="text.secondary"
                        >
                            Reservations until: {setLabel(reservationsUntilDay)}
                        </Typography>
                    </>
                )}
            </Typography>

            {!isNaN(valueOld) ? (
                <Stack
                    direction="row"
                    justifyContent="flex-start"
                    alignItems="center"
                    spacing={4}
                >
                    <Typography fontWeight={"bold"}>
                        {fns(hotelID, valueOld, valueFormat)}
                    </Typography>
                    <SmallPercentage
                        usePercentage={false}
                        brackets={false}
                        tooltip={false}
                        bubble={true}
                        bubbleRound={true}
                        actual={value - valueOld}
                        old={1}
                        text={fns(
                            hotelID,
                            parseFloat((value - valueOld).toFixed(2)),
                            valueFormat,
                        )}
                        showFormula={false}
                        isInverted={
                            (DISPLAY_ELEMENT_HELPER[metricName] ?? {}).upside ??
                            false
                        }
                        styles={{ fontSize: "12px" }}
                    />
                </Stack>
            ) : (
                <Typography fontSize={"1.25em"} fontStyle={"italic"}>
                    No previous value
                </Typography>
            )}

            {targetExtra}
            {extraContent && (
                <>
                    <Divider sx={{ my: 1, borderColor: "rgba(0,0,0,0.08)" }} />
                    <Typography fontSize="0.85em" color="text.secondary">
                        {extraContent}
                    </Typography>
                </>
            )}
        </Stack>
    );

    // Build context for custom renderers
    const ctx = {
        metricName,
        comparisonType,
        comparisonCustom,
        fromDay,
        toDay,
        value,
        valueOld,
        comparisonMeta,
        hotelID,
        comparison_label,
        setLabel,
    };

    return (
        <Tooltip
            componentsProps={{
                tooltip: {
                    sx: {
                        bgcolor: "white",
                        color: "black",
                        borderTop: `2px solid ${Colours.primary}`,
                        borderRadius: "4px",
                        boxShadow: "0px 0px 12px rgba(0,0,0,0.1)",
                    },
                },
                arrow: {
                    sx: {
                        color: "white",
                        fontSize: "1.75em",
                        "&:before": {
                            border: `2px solid ${Colours.primary}`,
                            borderBottomColor: "transparent",
                            borderRightColor: "transparent",
                        },
                    },
                },
            }}
            arrow
            // Keep the same shell; swap only the inner content if provided
            title={renderContent ? renderContent(ctx) : defaultContent}
            onMouseEnter={() => setSelected(true)}
            onMouseLeave={() => setSelected(false)}
        >
            {children}
        </Tooltip>
    );
};

export default TooltipComparison;
